<!-- Adapted from YesPlayMusic v0.4.11 src/views/album.vue (MIT, Copyright (c) 2020-2023 qier222). Original page structure and SCSS retained; data/actions use the local and Cloudflare runtime. -->
<template>
  <div class="album-page" :class="{ 'salt-detail': salt }">
    <div v-if="loading" class="page-state" role="status">正在载入专辑…</div><div v-else-if="error" class="page-state" role="alert"><p>{{ error }}</p><SaltButton v-if="salt" data-scroll-retry @click="loadData" style-type="subtle">重试</SaltButton><ButtonTwoTone v-else color="grey" data-scroll-retry @click.native="loadData">重试</ButtonTwoTone></div>
    <section v-else-if="hasAlbumChoices" class="album-versions" aria-label="选择专辑版本">
      <h1>{{ localName }}</h1><p>找到多个同名专辑，请选择要打开的版本。</p>
      <div class="album-version-list"><router-link v-for="version in albumVersions" :key="version.key" :to="{ name: 'album', params: { id: version.id }, query: localSourceFilter === 'all' ? {} : { source: localSourceFilter } }" class="album-version" :aria-label="'查看 ' + version.name + ' · ' + version.artists + ' · ' + version.platform">
        <SaltCard v-if="salt" class="version-card"><img :src="version.cover" alt="" loading="lazy" @error="fallbackVersionCover" /><div><strong>{{ version.name }}</strong><span>{{ version.artists }}</span><small>{{ version.platform }} · {{ version.count }} 首歌曲</small></div></SaltCard><template v-else><img :src="version.cover" alt="" loading="lazy" @error="fallbackVersionCover" /><div><strong>{{ version.name }}</strong><span>{{ version.artists }}</span><small>{{ version.platform }} · {{ version.count }} 首歌曲</small></div></template>
      </router-link></div>
    </section>
    <template v-else><div class="playlist-info"><Cover :source-filter="isLocal ? localSourceFilter : 'all'" :id="album.id" :image-url="coverUrl" :show-play-button="false" :always-show-shadow="!salt" :always-show-play-button="!salt" :fixed-size="salt ? 160 : 288" type="album" :cover-hover="false" /><div class="info"><div class="title">{{ album.name }}</div><div class="artist">专辑 · <ArtistsInLine :artists="artists" /></div><div class="date-and-count"><span v-if="album.publishTime">{{ new Date(album.publishTime).getFullYear() }} · </span><span class="album-loaded-count">{{ countLabel }}</span> · {{ duration }}</div><div v-if="album.description" class="description" :class="{ expanded: fullDescription }" tabindex="0" @click="fullDescription = !fullDescription" @keydown.enter="fullDescription = !fullDescription">{{ album.description }}</div><div class="buttons detail-actions"><SaltButton v-if="salt" :disabled="!tracks.length || !!completeAction" @click="playAll"><template #leading><svg-icon icon-class="play" /></template>播放</SaltButton><ButtonTwoTone v-else icon-class="play" :disabled="!tracks.length || !!completeAction" @click.native="playAll">播放</ButtonTwoTone><SaltButton v-if="salt" :disabled="!tracks.length || !!completeAction" @click="enqueueAll" style-type="subtle" class="detail-secondary" aria-label="加入队列" title="加入队列"><svg-icon class="detail-action-icon" icon-class="list" aria-hidden="true" /><span class="detail-action-label">加入队列</span></SaltButton><ButtonTwoTone v-else color="grey" :disabled="!tracks.length || !!completeAction" @click.native="enqueueAll" class="detail-secondary" aria-label="加入队列" title="加入队列"><svg-icon class="detail-action-icon" icon-class="list" aria-hidden="true" /><span class="detail-action-label">加入队列</span></ButtonTwoTone><SaltButton v-if="salt" :disabled="!tracks.length || !!completeAction" @click="saveAlbum" style-type="subtle" class="detail-secondary" aria-label="保存为歌单" title="保存为歌单"><svg-icon class="detail-action-icon" icon-class="plus" aria-hidden="true" /><span class="detail-action-label">保存为歌单</span></SaltButton><ButtonTwoTone v-else color="grey" :disabled="!tracks.length || !!completeAction" @click.native="saveAlbum" class="detail-secondary" aria-label="保存为歌单" title="保存为歌单"><svg-icon class="detail-action-icon" icon-class="plus" aria-hidden="true" /><span class="detail-action-label">保存为歌单</span></ButtonTwoTone></div></div></div><TrackList v-if="tracks.length" :id="album.id" :tracks="tracks" type="album" :album-object="album" :load-playback-tracks="collectTracks" /><div v-else class="page-state">此专辑暂无可读取的歌曲。</div><div v-if="!isLocal" class="album-page-tail"><p v-if="completeAction" class="page-state album-collecting" role="status">正在读取完整专辑，已加载 {{ remoteTracks.length }}{{ remoteTotal === null ? '' : ' / ' + remoteTotal }} 首…</p><p v-if="partial" class="page-state">上游未返回部分曲目，当前列表未包含完整专辑。</p><p v-if="moreError" class="page-state album-more-error" role="status">{{ moreError }} <SaltButton v-if="salt" style-type="plain" data-scroll-retry @click="retryMore">重试</SaltButton><button v-else type="button" data-scroll-retry @click="retryMore">重试</button></p><div v-if="nextCursor !== null" ref="prefetch" class="page-state album-prefetch" role="status">{{ loadingMore ? '正在载入更多歌曲…' : moreError ? '' : '继续滚动自动载入' }}</div></div><div v-if="album.company || album.publishTime" class="extra-info"><div v-if="album.publishTime" class="release-date">发行于 {{ new Date(album.publishTime).toLocaleDateString('zh-CN') }}</div><div v-if="album.company">{{ album.company }}</div></div><div v-if="moreAlbums.length" class="more-by"><div class="section-title">更多专辑</div><CoverRow :style="salt ? { gridTemplateColumns: 'repeat(auto-fill, minmax(128px, 1fr))' } : null" :sub-text-font-size="salt ? '14px' : '16px'" :gap="salt ? '16px' : '44px 24px'" :items="moreAlbums" type="album" sub-text="artist" :show-play-button="true" /></div></template>
  </div>
</template>
<script>
import { runtime } from '../bridge';
import { workspace } from '../workspace';
import { matchesLibrarySource, normalizeLibrarySource } from '../library-source';
import { albumGroupKey, albumRouteId, matchesAlbumRoute, trackPlatform } from '../album-identity';
import { trackArtistItems } from '../artist-navigation';
import { observeListPrefetch } from '../list-prefetch';
import ArtistsInLine from '../components/ArtistsInLine.vue';
import ButtonTwoTone from '../components/ButtonTwoTone.vue';
import SaltButton from '../salt/ui/SaltButton.vue';
import SaltCard from '../salt/ui/SaltCard.vue';
import TrackList from '../components/TrackList.vue';
import Cover from '../components/Cover.vue';
import CoverRow from '../components/CoverRow.vue';
export default {
  name: 'Album', components: { ButtonTwoTone, SaltButton, SaltCard, TrackList, Cover, CoverRow, ArtistsInLine },
  data() { return { workspace, state: runtime.state, remoteItem: {}, remoteTracks: [], moreAlbums: [], loading: false, error: '', fullDescription: false, requestId: 0, remoteTotal: null, nextCursor: null, partial: false, loadingMore: false, moreError: '', completeAction: '' }; },
  computed: {
    salt() { return this.workspace.appearance === 'salt'; },
    scrollRestoreState() { return { busy: this.loading || this.loadingMore, more: !this.isLocal && this.nextCursor !== null, failed: Boolean(this.error || this.moreError) }; },
    routeId() { return String(this.$route.params.id || ''); },
    isLocal() { return this.routeId.startsWith('local:'); },
    localName() { try { return decodeURIComponent(this.routeId.slice(6)); } catch { return this.routeId.slice(6); } },
    localSourceFilter() { return normalizeLibrarySource(this.$route.query.source); },
    localCandidates() { return this.isLocal ? runtime.libraryTracks().filter(t => matchesAlbumRoute(t, this.routeId) && matchesLibrarySource(t, this.localSourceFilter)) : []; },
    albumVersions() {
      if (!this.isLocal || this.routeId.startsWith('local:album:')) return [];
      const versions = new Map();
      for (const track of this.localCandidates) {
        const key = albumGroupKey(track);
        if (!versions.has(key)) versions.set(key, { key, id: albumRouteId(track), name: track.album || '未知专辑', artists: (track.artists || ['未知艺术家']).join(' / '), cover: runtime.cover(track), platform: ({ tx: 'QQ音乐', wy: '网易云音乐', local: '本地音乐', webdav: 'WebDAV' })[trackPlatform(track) || track.sourceType] || '音乐库', count: 0 });
        versions.get(key).count++;
      }
      return [...versions.values()];
    },
    hasAlbumChoices() { return this.albumVersions.length > 1; },
    tracks() { return this.isLocal ? this.hasAlbumChoices ? [] : this.localCandidates : this.remoteTracks; },
    album() { return this.isLocal ? { id: this.routeId, name: this.tracks[0]?.album || (this.routeId.startsWith('local:album:') ? '未知专辑' : this.localName), artists: Array.from(new Set(this.tracks.flatMap(t => t.artists))).map(name => ({ id: 'local:' + encodeURIComponent(name), name })) } : this.remoteItem; },
    artists() { const platform = this.isLocal ? '' : this.routeId.startsWith('qq:') ? 'tx' : 'wy'; const artists = this.album.artists || (this.album.artist ? [this.album.artist] : []); return artists.map(artist => { const matching = this.tracks.flatMap(trackArtistItems).find(item => item.name === artist.name && item.id); return { ...artist, id: matching?.id || artist.id, platform: matching?.platform || platform }; }); },
    coverUrl() { return this.album.picUrl || runtime.cover(this.tracks[0]); },
    duration() { return runtime.formatTime(this.tracks.reduce((sum, track) => sum + (track.durationMs || 0), 0)); },
    countLabel() { return this.isLocal || this.remoteTotal === null ? this.tracks.length + ' 首歌曲' : '共 ' + this.remoteTotal + ' 首歌曲' + (this.tracks.length !== this.remoteTotal ? ' · 已加载 ' + this.tracks.length + ' 首' : ''); },
  },
  watch: { routeId: { immediate: true, handler() { this.loadData(); } } },
  mounted() { this.bindPrefetch(); },
  beforeDestroy() { this.requestId++; this._disposePrefetch?.(); },
  methods: {
    fallbackVersionCover(event) { const image = event.currentTarget; const fallback = runtime.cover(); if (image.dataset.coverFallback === 'true' || image.getAttribute('src') === fallback) return; image.dataset.coverFallback = 'true'; image.src = fallback; },
    bindPrefetch() {
      this._disposePrefetch?.();
      this.$nextTick(() => {
        this._disposePrefetch?.();
        if (this.isLocal || this.loading || !this.$refs.prefetch) return;
        this._disposePrefetch = observeListPrefetch(this.$refs.prefetch, () => {
          if (!this.moreError && !this.completeAction) void this.loadMore().catch(() => {});
        });
      });
    },
    applyPage(data, offset = '0', append = false) {
      const next = data.more ? String(data.nextCursor ?? '') : null;
      if (next !== null && (!/^\d+$/.test(next) || !Number.isSafeInteger(Number(next)) || Number(next) <= Number(offset) || this._cursors.has(next))) throw new Error('专辑分页没有前进，请重试');
      const tracks = append ? [...this.remoteTracks, ...(data.tracks || [])] : data.tracks || [];
      const unique = [...new Map(tracks.map(track => [track.id, track])).values()];
      if (append && next !== null && unique.length === this.remoteTracks.length) throw new Error('专辑分页没有前进，请重试');
      this.remoteTracks = unique;
      if (data.total != null && Number.isFinite(Number(data.total))) this.remoteTotal = Math.max(this.remoteTotal ?? 0, Number(data.total));
      this.nextCursor = next; this.partial = this.partial || Boolean(data.partial); this._cursors.add(String(offset));
    },
    async loadData() {
      const ticket = ++this.requestId, routeId = this.routeId;
      this._disposePrefetch?.(); this._pagePromise = null; this._collectPromise = null; this._cursors = new Set();
      this.error = ''; this.fullDescription = false; this.moreAlbums = []; this.remoteTotal = null; this.nextCursor = null; this.partial = false;
      this.loadingMore = false; this.moreError = ''; this.completeAction = '';
      if (this.isLocal) { this.loading = false; return; }
      this.remoteItem = {}; this.remoteTracks = []; this.loading = true;
      try {
        const data = await runtime.catalog('album', routeId, { offset: 0, limit: 100 });
        if (ticket !== this.requestId || routeId !== this.routeId) return;
        if (!data.item) throw new Error('没有找到这个专辑');
        this.remoteItem = data.item; this.applyPage(data); this.moreAlbums = data.albums || [];
      } catch (error) { if (ticket === this.requestId) this.error = error.message || '专辑载入失败'; }
      finally { if (ticket === this.requestId) { this.loading = false; this.bindPrefetch(); } }
    },
    retryMore() { return this.nextCursor === null ? this.loadData() : this.loadMore().catch(() => {}); },
    loadMore() {
      if (this._pagePromise) return this._pagePromise;
      if (this.nextCursor === null || this.isLocal) return Promise.resolve();
      const ticket = this.requestId, routeId = this.routeId, cursor = this.nextCursor;
      this.loadingMore = true; this.moreError = '';
      const operation = (async () => {
        try {
          const data = await runtime.catalog('album', routeId, { offset: cursor, limit: 100 });
          if (ticket !== this.requestId || routeId !== this.routeId) throw new DOMException('专辑已切换', 'AbortError');
          this.applyPage(data, cursor, true);
        } catch (error) { if (ticket === this.requestId) this.moreError = error.message || '后续歌曲读取失败'; throw error; }
        finally { if (ticket === this.requestId) { this.loadingMore = false; this._pagePromise = null; if (!this.moreError) this.bindPrefetch(); } }
      })();
      this._pagePromise = operation; return operation;
    },
    collectTracks() {
      if (this.isLocal) return Promise.resolve([...this.tracks]);
      if (this._collectPromise) return this._collectPromise;
      const ticket = this.requestId;
      const operation = (async () => {
        while (this.nextCursor !== null) { await this.loadMore(); if (ticket !== this.requestId) throw new DOMException('专辑已切换', 'AbortError'); }
        if (this.partial || this.remoteTotal !== null && this.remoteTracks.length < this.remoteTotal) {
          this.moreError = '专辑尚未完整读取，请重试后再播放或保存全部'; throw new Error(this.moreError);
        }
        return [...this.tracks];
      })().finally(() => { if (ticket === this.requestId) this._collectPromise = null; });
      this._collectPromise = operation; return operation;
    },
    async runComplete(action, execute, current = () => true) {
      if (this.completeAction) return;
      const ticket = this.requestId; this.completeAction = action;
      try { const tracks = await this.collectTracks(); if (ticket === this.requestId && current() && tracks.length) await execute(tracks); }
      catch (error) { if (ticket === this.requestId && current() && error.name !== 'AbortError') runtime.notify('未执行' + action + '：' + (error.message || '专辑读取失败，请重试'), 'error'); }
      finally { if (ticket === this.requestId) { this.completeAction = ''; this.bindPrefetch(); } }
    },
    playAll() {
      if (this.completeAction) return;
      const playbackTicket = runtime.reservePlaybackRequest();
      return this.runComplete('播放全部', tracks => runtime.playTracks(tracks), () => runtime.isPlaybackRequestCurrent(playbackTicket));
    },
    enqueueAll() { return this.runComplete('加入队列', tracks => runtime.enqueueTracks(tracks)); },
    saveAlbum() { return this.runComplete('保存专辑', async tracks => { await runtime.createPlaylist(this.album.name, tracks); runtime.notify('已保存为歌单'); }); },
  },
};
</script>
<style lang="scss" scoped>
.album-versions { color: var(--color-text); }
.album-versions h1 { font-size: clamp(30px, 4vw, 48px); margin: 0 0 12px; }
.album-versions > p { color: var(--color-secondary); margin: 0 0 28px; }
.album-version-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 20px; }
.album-version { display: flex; align-items: center; gap: 18px; padding: 16px; border-radius: 14px; background: var(--color-secondary-bg); color: inherit; min-width: 0; }
.album-version:hover { background: var(--color-primary-bg); }
.album-version:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 3px; }
.album-version img { width: 88px; height: 88px; object-fit: cover; border-radius: 10px; flex-shrink: 0; }
.album-version > div { display: grid; gap: 6px; min-width: 0; overflow-wrap: anywhere; }
.album-version strong { font-size: 19px; }
.album-version span, .album-version small { color: var(--color-secondary); line-height: 1.5; }
@media(max-width: 420px) { .album-version-list { grid-template-columns: minmax(0, 1fr); } .album-version { padding: 12px; gap: 12px; } .album-version img { width: 72px; height: 72px; } }
.page-state { padding: 48px 0; color: var(--color-text); opacity: .78; line-height: 1.8; }
.page-state a { color: var(--color-primary); }
.playlist-info .info { min-width: 0; }
.playlist-info .info .description.expanded { -webkit-line-clamp: unset; white-space: pre-line; }
.playlist-info .buttons { flex-wrap: wrap; gap: 10px 0; }
button:disabled { opacity: .4; cursor: default; }
@media(max-width: 800px) { .playlist-info { width: 100%; flex-direction: column; margin-bottom: 36px; align-items: flex-start; } .playlist-info .info { margin: 24px 0 0; } .playlist-info .info .title { font-size: 32px; overflow-wrap: anywhere; } .playlist-info .info .artist { margin-top: 14px; } }

.album-page {
  margin-top: 32px;
}
.playlist-info {
  display: flex;
  width: 78vw;
  margin-bottom: 72px;
  .info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1;
    margin-left: 56px;
    color: var(--color-text);
    .title {
      font-size: 56px;
      font-weight: 700;
    }
    .subtitle {
      font-size: 22px;
      font-weight: 600;
    }
    .artist {
      font-size: 18px;
      opacity: 0.88;
      margin-top: 24px;
      a {
        font-weight: 600;
      }
    }
    .date-and-count {
      font-size: 14px;
      opacity: 0.68;
      margin-top: 2px;
    }
    .description {
      user-select: none;
      font-size: 14px;
      opacity: 0.68;
      margin-top: 24px;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      cursor: pointer;
      white-space: pre-line;
      &:hover {
        transition: opacity 0.3s;
        opacity: 0.88;
      }
    }
    .buttons {
      margin-top: 32px;
      display: flex;
      button {
        margin-right: 16px;
      }
    }
  }
}
.disc {
  color: var(--color-text);
}

.explicit-symbol {
  opacity: 0.28;
  color: var(--color-text);
  margin-right: 4px;
  .svg-icon {
    margin-bottom: -3px;
  }
}

.extra-info {
  margin-top: 36px;
  margin-bottom: 36px;
  font-size: 12px;
  opacity: 0.48;
  color: var(--color-text);
  div {
    margin-bottom: 4px;
  }
  .album-time {
    opacity: 0.68;
  }
}

.more-by {
  border-top: 1px solid rgba(128, 128, 128, 0.18);

  padding-top: 22px;
  .section-title {
    font-size: 22px;
    font-weight: 600;
    opacity: 0.88;
    color: var(--color-text);
    margin-bottom: 20px;
  }
}
.description-fulltext {
  font-size: 16px;
  margin-top: 24px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-line;
}

</style>

<style lang="scss" scoped>
/* Keep the mature album detail and version identity, with SaltUI metrics. */
.album-page.salt-detail {
  margin-top:16px; color:var(--salt-text); font-size:var(--salt-text-main,14px);
  .playlist-info { width:100%; flex-direction:row; align-items:flex-start; gap:16px; margin-bottom:24px; }
  .playlist-info > .cover { flex:0 0 160px; width:160px; }
  .playlist-info .info { margin:0; min-width:0; }
  .playlist-info .info .title { font-size:var(--salt-text-title,24px); line-height:1.35; font-weight:500; overflow-wrap:anywhere; }
  .playlist-info .info .artist { margin-top:8px; color:var(--salt-subtext); font-size:14px; line-height:1.5; opacity:1; }
  .playlist-info .info .artist a { font-weight:400; }
  .playlist-info .info .date-and-count { margin-top:2px; color:var(--salt-subtext); font-size:12px; line-height:1.5; opacity:1; }
  .playlist-info .info .description { margin-top:12px; color:var(--salt-subtext); font-size:14px; line-height:1.5; opacity:1; overflow-wrap:anywhere; }
  .playlist-info .info .buttons { margin-top:16px; gap:8px; }
  .playlist-info .info .buttons button { margin:0; }
  .salt-ui-button:disabled { opacity:1; }
  .page-state { padding:24px 0; color:var(--salt-subtext); font-size:14px; opacity:1; }
  .extra-info { margin:16px 0; color:var(--salt-subtext); font-size:12px; line-height:1.5; opacity:1; }
  .more-by { padding-top:16px; border-top-color:var(--salt-stroke); }
  .more-by .section-title { margin:0 0 12px; color:var(--salt-text); font-size:16px; font-weight:500; line-height:1.5; opacity:1; }
  .album-versions h1 { margin:0 0 8px; font-size:24px; line-height:1.35; font-weight:500; overflow-wrap:anywhere; }
  .album-versions > p { margin-bottom:16px; color:var(--salt-subtext); font-size:14px; line-height:1.5; }
  .album-version-list { grid-template-columns:repeat(auto-fit,minmax(240px,1fr)); gap:12px; }
  .album-version { display:block; padding:0; border-radius:6px; background:transparent; }
  .album-version:focus-visible { outline:1px solid var(--salt-accent); outline-offset:2px; }
  .album-version:hover ::v-deep .salt-ui-card { background:var(--salt-subtle-background); }
  .version-card ::v-deep .salt-ui-card { flex-direction:row; align-items:center; gap:12px; padding:12px; }
  .version-card ::v-deep .salt-ui-card > div { display:grid; gap:2px; min-width:0; overflow-wrap:anywhere; }
  .album-version img { width:64px; height:64px; border-radius:4px; }
  .album-version strong { font-size:14px; line-height:1.5; font-weight:500; }
  .album-version span,.album-version small { color:var(--salt-subtext); font-size:12px; }
  ::v-deep .cover img { display:block; box-sizing:border-box; border-radius:6px; border-color:var(--salt-stroke); }
  ::v-deep .cover .shadow { display:none; }
  ::v-deep .cover .play-button { position:relative; width:32px !important; height:32px !important; padding:0; border:0; border-radius:9999px; background:var(--salt-accent); color:var(--salt-on-accent); backdrop-filter:none; box-shadow:none; transform:none; transition:none; overflow:hidden; isolation:isolate; }
  ::v-deep .cover .play-button::after { content:''; position:absolute; inset:0; pointer-events:none; }
  ::v-deep .cover .play-button:not(:disabled):hover::after,::v-deep .cover .play-button:not(:disabled):focus-visible::after { background:var(--salt-hover-overlay); }
  ::v-deep .cover .play-button:not(:disabled):active::after { background:var(--salt-press-overlay); }
  ::v-deep .cover .play-button .svg-icon { width:16px; height:16px; margin:0; }
  ::v-deep .cover-row .item .text .title { font-weight:400; line-height:1.5; }
  ::v-deep .cover-row .item .text .info { color:var(--salt-subtext); opacity:1; }
}

/* The same detail component becomes a phone page, without a second data view. */
#app.salt-mobile-layout .album-page.salt-detail {
  --detail-mobile-cover-size:144px;
  margin-top:12px;
  .playlist-info { flex-direction:column; align-items:stretch; gap:16px; margin:0 0 20px; }
  .playlist-info > .cover { align-self:center; flex:0 0 auto; width:var(--detail-mobile-cover-size) !important; height:var(--detail-mobile-cover-size) !important; }
  .playlist-info > .cover ::v-deep .cover-container { width:100%; height:100%; }
  .playlist-info > .cover ::v-deep img { width:100% !important; height:100% !important; object-fit:cover; }
  .playlist-info .info { flex:none; width:100%; margin:0; }
  .playlist-info .info .title { font-size:24px; line-height:1.3; overflow-wrap:anywhere; }
  .playlist-info .info .artist,.playlist-info .info .date-and-count { overflow-wrap:anywhere; }
  .playlist-info .info .buttons { margin-top:12px; gap:8px; }
  .salt-ui-button { min-width:44px; min-height:44px; max-width:100%; }
  .buttons ::v-deep .label { white-space:normal; overflow-wrap:anywhere; }
  .album-version-list { grid-template-columns:minmax(0,1fr); }
  @media (orientation:landscape) and (max-height:599px) {
    --detail-mobile-cover-size:96px;
    .playlist-info { flex-direction:row; align-items:flex-start; gap:16px; margin-bottom:16px; }
    .playlist-info > .cover { align-self:flex-start; flex-basis:var(--detail-mobile-cover-size); }
    .playlist-info .info { flex:1; width:auto; }
    .playlist-info .info .title { font-size:22px; }
    .playlist-info .info .artist { margin-top:4px; }
    .playlist-info .info .buttons { margin-top:8px; }
  }
}

@media (min-width:600px) and (max-width:1023px) and (max-height:599px) {
  #app.salt-shell .album-page.salt-detail {
    .playlist-info { flex-direction:row; align-items:flex-start; gap:16px; margin-bottom:16px; }
    .playlist-info > .cover { align-self:flex-start; flex:0 0 96px; width:96px; height:96px; }
    .playlist-info .info { flex:1; width:auto; }
    .playlist-info .info .title { font-size:22px; }
    .playlist-info .info .artist { margin-top:4px; }
    .playlist-info .info .buttons { margin-top:8px; }
    .salt-ui-button { min-width:44px; min-height:44px; max-width:100%; }
  }
}
</style>

<style lang="scss" scoped>
/* Phones retain one action rail. Secondary commands collapse to named icons,
   while the primary Play command and desktop labels remain readable. */
.detail-action-icon { display:none; }
@media (max-width:599px), (max-width:1023px) and (max-height:599px) and (pointer:coarse) {
  .album-page {
    .playlist-info .buttons.detail-actions { display:flex; flex-wrap:nowrap; gap:6px; min-width:0; width:100%; }
    .buttons.detail-actions > button { flex:none; min-width:44px; min-height:44px; height:44px; margin:0; padding-inline:12px !important; font-size:14px; white-space:nowrap; }
    .buttons.detail-actions > button.detail-secondary { flex:0 0 44px; width:44px; padding:0 !important; }
    .detail-secondary .detail-action-label { display:none; }
    .detail-action-icon { display:block; flex:none; width:20px; height:20px; margin:0 !important; }
    .buttons.detail-actions ::v-deep .label { white-space:nowrap; }
    
  }
}
@media(max-width:599px) {
  .album-page:not(.salt-detail) {
    .playlist-info { width:100%; flex-direction:column; align-items:stretch; gap:16px; margin-bottom:20px; }
    .playlist-info .info { flex:none; width:100%; min-width:0; margin:0; }
    .playlist-info > .cover { align-self:center; width:144px !important; height:144px !important; }
    .playlist-info > .cover ::v-deep img { width:144px !important; height:144px !important; object-fit:cover; }
  }
}
</style>
